import * as v0 from "@aidkitorg/types/lib/survey";

export type FollowUpSelections = Record<string, Record<string, string>>;

type CustomDecision = v0.SharedDecision | v0.IndividualDecision;

/**
 * The follow up attached to the decision currently selected for an applicant, if any.
 * A follow up with no options is treated as absent so the gate, hints, and
 * payload (incl. its clear-on-deselect branch) all agree there is nothing to answer.
 */
export function followUpFor(
  customDecisions: CustomDecision[] | undefined,
  decisionValue: string | undefined
): v0.FollowUpDecision | undefined {
  if (!decisionValue) return undefined;
  const decision = (customDecisions || []).find(d => d.value === decisionValue);
  const followUp = (decision as v0.SharedDecision | undefined)?.followUp;
  return followUp?.options?.length ? followUp : undefined;
}

/** Every follow up field configured across all decisions. */
export function allFollowUpFields(customDecisions: CustomDecision[] | undefined): string[] {
  const fields = (customDecisions || [])
    .map(d => (d as v0.SharedDecision).followUp?.field)
    .filter((f): f is string => !!f);
  return fields.filter((f, i) => fields.indexOf(f) === i);
}

/**
 * Answered = non-empty ('' always reads as unset, even as a configured option
 * value) and one of this follow up's own options. Shared by the required hint,
 * the confirm gate, and the payload so they agree.
 */
export function followUpAnswered(followUp: v0.FollowUpDecision | undefined, value: string | undefined): boolean {
  return !!value && !!followUp?.options?.some(option => option.value === value);
}

/**
 * Follow up writes for compute_or_modify_identities. Absent key = untouched,
 * '' = explicit clear. Only currently-flagged same-program applicants (`uids`)
 * are ever written, and a clear is only emitted for a field this review made
 * active via the SAVED decision and the session has now made inactive — values
 * this review never owned (other reviews, other writers) are left untouched.
 */
export function buildFollowUpPayload(params: {
  customDecisions: CustomDecision[] | undefined,
  /** currently-flagged, same-program compared applicant uids */
  uids: string[],
  /** uid -> selected top level decision value */
  decisions: Record<string, string>,
  /** uid -> decision value parsed from the saved target field */
  savedDecisions: Record<string, string>,
  selections: FollowUpSelections,
  thisUid: string,
  /** uid -> field -> saved value, used to emit explicit clears */
  savedValues: Record<string, Record<string, string | undefined>>,
}): Record<string, Record<string, string>> {
  const { customDecisions, uids, decisions, savedDecisions, selections, thisUid, savedValues } = params;
  const payload: Record<string, Record<string, string>> = {};

  const put = (uid: string, field: string, value: string) => {
    payload[uid] ||= {};
    payload[uid][field] = value;
  };

  // Resolve each compared applicant's active follow up once, now and as saved
  const activeByUid: Record<string, v0.FollowUpDecision | undefined> = {};
  const savedActiveByUid: Record<string, v0.FollowUpDecision | undefined> = {};
  for (const uid of uids) {
    activeByUid[uid] = followUpFor(customDecisions, decisions[uid]);
    savedActiveByUid[uid] = followUpFor(customDecisions, savedDecisions[uid]);
  }

  for (const field of allFollowUpFields(customDecisions)) {
    for (const uid of uids) {
      const followUp = activeByUid[uid]?.field === field ? activeByUid[uid] : undefined;
      const wasActive = savedActiveByUid[uid]?.field === field;
      const selected = selections[uid]?.[field];
      if (followUp && followUpAnswered(followUp, selected)) {
        put(uid, field, selected!);
      } else if (!followUp && wasActive && savedValues[uid]?.[field]) {
        put(uid, field, '');
      }
    }

    // This applicant: active while any compared applicant has a parent with this field
    const activeFollowUps = uids
      .map(uid => activeByUid[uid])
      .filter((f): f is v0.FollowUpDecision => !!f && f.field === field);
    const wasActiveAnywhere = uids.some(uid => savedActiveByUid[uid]?.field === field);
    const thisSelected = selections[thisUid]?.[field];
    if (activeFollowUps.some(followUp => followUpAnswered(followUp, thisSelected))) {
      put(thisUid, field, thisSelected!);
    } else if (!activeFollowUps.length && wasActiveAnywhere && savedValues[thisUid]?.[field]) {
      put(thisUid, field, '');
    }
  }

  return payload;
}

/**
 * Complete = a decision is selected and, if it has a follow up with options,
 * both applicants answered with one of THAT follow up's options.
 */
export function appFullyDecided(params: {
  customDecisions: CustomDecision[] | undefined,
  uid: string,
  thisUid: string,
  decisions: Record<string, string>,
  selections: FollowUpSelections,
}): boolean {
  const { customDecisions, uid, thisUid, decisions, selections } = params;
  const decisionValue = decisions[uid];
  if (!decisionValue) return false;

  const followUp = followUpFor(customDecisions, decisionValue);
  if (!followUp) return true;

  return followUpAnswered(followUp, selections[uid]?.[followUp.field])
    && followUpAnswered(followUp, selections[thisUid]?.[followUp.field]);
}

/**
 * Whether confirming would write a follow up value that differs from saved —
 * follow ups aren't in the target field encoding, so the button needs this.
 */
export function followUpsDirty(params: {
  customDecisions: CustomDecision[] | undefined,
  uids: string[],
  decisions: Record<string, string>,
  savedDecisions: Record<string, string>,
  selections: FollowUpSelections,
  thisUid: string,
  savedValues: Record<string, Record<string, string | undefined>>,
}): boolean {
  const payload = buildFollowUpPayload(params);
  return Object.keys(payload).some(uid =>
    Object.keys(payload[uid]).some(field =>
      (params.savedValues[uid]?.[field] || '') !== payload[uid][field]));
}
